import * as Sentry from '@sentry/react'
import Amplify from '@aws-amplify/core'
import environment from '_environments'
import LogRocket from 'logrocket'
import { clarity } from 'react-microsoft-clarity'
import TagManager from 'react-gtm-module'
import posthog from 'posthog-js'
import pkg from '../../package.json'

export * from './user.service'
export * from './icons'

let isSentryInitialized = false
let posthogClient = null

// Initialize PostHog once with full configuration
export function getPostHogClient() {
  if (posthogClient) {
    return posthogClient
  }

  if (!environment.keys.posthog) {
    return null
  }

  try {
    posthog.init(environment.keys.posthog, {
      api_host: 'https://spaceraid.myassetmap.com',
      ui_host: 'https://us.posthog.com',
      // Enable all features by default - AnalyticsProvider will control usage via config
      autocapture: true,
      capture_pageview: true,
    })
    posthogClient = posthog
    return posthogClient
  } catch (error) {
    console.error('Failed to initialize PostHog:', error)
    return null
  }
}

export function configureSentry() {
  if (isSentryInitialized) {
    return
  }

  // Initialize PostHog first for the Sentry integration
  const postHogInstance = getPostHogClient()

  // Sentry
  Sentry.init({
    dsn: environment.keys.sentry,
    // v10.4+: user IP is only attached when sendDefaultPii is true
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.replayCanvasIntegration(),
      // Only add PostHog integration if PostHog is available and initialized
      ...(postHogInstance && environment.keys.posthog
        ? [
            posthog.sentryIntegration({
              organization: 'myassetmap',
              projectId: '4506713766428672',
              prefix: 'https://myassetmap.sentry.io/organizations/',
              severityAllowList: ['error', 'info'], // optional: here is set to handle captureMessage (info) and captureException (error)
            }),
          ]
        : []),
    ],

    environment: environment.env,
    release: `myassetmap-frontend@${pkg.version}-${environment.env}`,

    // Errors thrown entirely inside third-party HubSpot scripts (tracking,
    // banner, forms, conversations) are not actionable from our codebase —
    // e.g. Sentry FRONTEND-7N, where HubSpot's URL-param parser throws on
    // malformed inbound query strings. Keep them out of Sentry. (MAM-794)
    denyUrls: [
      /hs-scripts\.com/i,
      /hs-analytics\.net/i,
      /hs-banner\.com/i,
      /hscollectedforms\.net/i,
      /usemessages\.com/i,
      /js\.hubspot\.com/i,
      // HubSpot analytics bundle as it appears in stack frames when the
      // domain is stripped: /analytics/{timestamp}/{portalId}.js
      /\/analytics\/\d+\/245161158\.js/,
    ],

    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/*myassetmap\.com\//],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    enabled: environment.build !== 'development',
  })

  isSentryInitialized = true
}

export function configureAmplify() {
  const { aws } = environment

  const config = {
    Auth: {
      mandatorySignIn: true,
      region: aws.region,
      userPoolId: aws.userPoolId,
      identityPoolId: aws.identityPoolId,
      userPoolWebClientId: aws.userPoolWebClientId,
    },
    API: {
      endpoints: [
        {
          name: 'login',
          endpoint: aws.awsApiAmplifyUrl,
          region: aws.region,
        },
        {
          name: 'my-asset-map-api',
          endpoint: aws.awsApiAmplifyUrl2,
          region: aws.region,
          authorizationType: 'AMAZON_COGNITO_USER_POOLS',
        },
      ],
    },
  }

  Amplify.configure(config)
}

export function configureAnalytics() {
  // Skip analytics in development environments
  const status = {
    logrocket: false,
    clarity: false,
    gtm: false,
  }

  // Skip analytics in development environments
  if (environment.isLocal || environment.isDev) {
    console.info('Analytics not initialized in development')
    return status
  }

  // LogRocket
  if (environment.keys.logrocket) {
    try {
      LogRocket.init(environment.keys.logrocket)
      status.logrocket = true
    } catch (error) {
      console.error('Failed to initialize LogRocket:', error)
    }
  }

  // Clarity
  if (environment.keys.clarity) {
    try {
      clarity.init(environment.keys.clarity)
      status.clarity = true
    } catch (error) {
      console.error('Failed to initialize Clarity:', error)
    }
  }

  // GTM
  if (environment.keys.gtm) {
    try {
      TagManager.initialize({ gtmId: environment.keys.gtm })
      status.gtm = true
    } catch (error) {
      console.error('Failed to initialize GTM:', error)
    }
  }

  return status
}
